import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Shield, AlertTriangle, FileText, RefreshCw, ChevronRight, Clock, CheckCircle, Activity } from 'lucide-react';
import api from '../../lib/api';

const SEV_COLORS = { CRITICAL: 'bg-red-600 text-white', HIGH: 'bg-orange-500 text-white', MEDIUM: 'bg-yellow-500 text-black', LOW: 'bg-slate-500 text-white' };
const MOD_ICONS = { service: Activity, timesheet: Clock, invoice: FileText, documentation: Shield };
const MOD_COLORS = { service: 'text-blue-400', timesheet: 'text-purple-400', invoice: 'text-emerald-400', documentation: 'text-amber-400' };

export default function ComplianceDashboardPage() {
  const navigate = useNavigate();
  const [scan, setScan] = useState(null);
  const [patterns, setPatterns] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('violations');
  const [auditEntity, setAuditEntity] = useState(null);
  const [auditData, setAuditData] = useState(null);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [sc, pt] = await Promise.all([
        api.get('/compliance/scan'),
        api.get('/compliance/risk-patterns'),
      ]);
      if (sc.data?.data) setScan(sc.data.data);
      if (pt.data?.data) setPatterns(pt.data.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const fetchAuditTrail = async (entityType, entityId) => {
    console.log('fetchAuditTrail called:', entityType, entityId);
    setAuditEntity({ type: entityType, id: entityId });
    setActiveTab('audit');
    try {
      const res = await api.get(`/compliance/audit-trail/${entityType}/${entityId}`);
      console.log('Audit trail response:', res.data);
      if (res.data?.data) {
        console.log('Setting auditData:', res.data.data);
        setAuditData(res.data.data);
      }
    } catch (e) { 
      console.error('Audit trail error:', e); 
    }
  };

  useEffect(() => { fetchAll(); }, []);

  if (loading) return <div className="min-h-screen bg-[#0a0a0f] flex items-center justify-center"><div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-500 border-t-transparent" /></div>;

  const violations = scan?.violations || [];
  const bySev = scan?.by_severity || {};
  const byMod = scan?.by_module || {};

  return (
    <div className="min-h-screen bg-[#0a0a0f] text-white" data-testid="compliance-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2"><Shield className="h-6 w-6 text-blue-400" />Compliance & Audit</h1>
            <p className="text-sm text-slate-400 mt-1">NDIS compliance monitoring and audit readiness</p>
          </div>
          <Button onClick={fetchAll} className="bg-blue-600 hover:bg-blue-700" data-testid="refresh-compliance-btn"><RefreshCw className="h-4 w-4 mr-2" />Scan</Button>
        </div>

        {/* Severity Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {['CRITICAL', 'HIGH', 'MEDIUM', 'LOW'].map(level => (
            <Card key={level} className="bg-[#12121a] border-[#1e1e2e]" data-testid={`sev-${level.toLowerCase()}`}>
              <CardContent className="p-4 flex items-center justify-between">
                <div>
                  <p className="text-xs text-slate-500">{level}</p>
                  <p className="text-2xl font-bold mt-1">{bySev[level] || 0}</p>
                </div>
                <Badge className={`${SEV_COLORS[level]} text-xs`}>{level}</Badge>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Module Breakdown */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {['service', 'timesheet', 'invoice', 'documentation'].map(mod => {
            const Icon = MOD_ICONS[mod];
            return (
              <Card key={mod} className="bg-[#12121a] border-[#1e1e2e]" data-testid={`mod-${mod}`}>
                <CardContent className="p-4 flex items-center gap-3">
                  <Icon className={`h-5 w-5 ${MOD_COLORS[mod]}`} />
                  <div>
                    <p className="text-xs text-slate-500 capitalize">{mod}</p>
                    <p className="text-lg font-semibold">{byMod[mod] || 0}</p>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Tabs */}
        <div className="flex gap-1 border-b border-[#1e1e2e]">
          {[{ id: 'violations', label: `Violations (${scan?.total || 0})` }, { id: 'patterns', label: `Risk Patterns (${patterns?.total || 0})` }, { id: 'audit', label: 'Audit Trail' }].map(t => (
            <button key={t.id} onClick={() => setActiveTab(t.id)}
              className={`px-4 py-2 text-sm border-b-2 transition-colors ${activeTab === t.id ? 'border-blue-500 text-blue-400' : 'border-transparent text-slate-500 hover:text-slate-300'}`}
              data-testid={`tab-${t.id}`}
            >{t.label}</button>
          ))}
        </div>

        {/* Violations Tab */}
        {activeTab === 'violations' && (
          <Card className="bg-[#12121a] border-[#1e1e2e]">
            <CardContent className="p-0">
              {violations.length === 0 ? (
                <div className="p-8 text-center text-emerald-400 flex items-center justify-center gap-2"><CheckCircle className="h-5 w-5" /> All compliance checks passed</div>
              ) : (
                <div className="divide-y divide-[#1e1e2e]">
                  {violations.map((v, i) => {
                    const Icon = MOD_ICONS[v.module] || Shield;
                    return (
                      <div key={i} className="flex items-center gap-3 px-4 py-3 hover:bg-[#1a1a25] transition-colors" data-testid={`violation-${i}`}>
                        <Icon className={`h-4 w-4 shrink-0 ${MOD_COLORS[v.module]}`} />
                        <Badge className={`${SEV_COLORS[v.severity]} text-[10px] px-1.5 shrink-0`}>{v.severity}</Badge>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-slate-200">{v.rule.replace(/_/g, ' ')}</p>
                          <p className="text-[10px] text-slate-500 truncate">{v.detail}</p>
                        </div>
                        {v.entity_code && <span className="text-[10px] text-slate-600 font-mono shrink-0">{v.entity_code}</span>}
                        {v.entity_id && (
                          <Button variant="ghost" size="sm" className="text-blue-400 text-[10px] h-6 px-2" onClick={() => fetchAuditTrail(v.entity_type, v.entity_id)}>Audit</Button>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Risk Patterns Tab */}
        {activeTab === 'patterns' && (
          <Card className="bg-[#12121a] border-[#1e1e2e]">
            <CardContent className="p-0">
              {(patterns?.patterns || []).length === 0 ? (
                <div className="p-8 text-center text-emerald-400 flex items-center justify-center gap-2"><CheckCircle className="h-5 w-5" /> No risk patterns detected</div>
              ) : (
                <div className="divide-y divide-[#1e1e2e]">
                  {(patterns?.patterns || []).map((p, i) => (
                    <div key={i} className="flex items-center gap-3 px-4 py-3" data-testid={`pattern-${i}`}>
                      <AlertTriangle className="h-4 w-4 text-amber-400 shrink-0" />
                      <Badge className={`${SEV_COLORS[p.severity]} text-[10px] px-1.5 shrink-0`}>{p.severity}</Badge>
                      <div className="flex-1"><p className="text-sm text-slate-200">{p.pattern.replace(/_/g, ' ')}</p><p className="text-[10px] text-slate-500">{p.detail}</p></div>
                      <p className="text-[10px] text-blue-400 max-w-[200px] text-right shrink-0">{p.suggestion}</p>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Audit Trail Tab */}
        {activeTab === 'audit' && (
          <div className="space-y-4">
            {!auditData ? (
              <Card className="bg-[#12121a] border-[#1e1e2e]"><CardContent className="p-8 text-center text-slate-500 text-sm">Click "Audit" on a violation to view its trail, or enter an entity ID below</CardContent></Card>
            ) : (
              <Card className="bg-[#12121a] border-[#1e1e2e]">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm text-slate-300">
                    Audit Trail: {auditData.entity_type} / {auditData.entity_id?.slice(0, 12)}...
                  </CardTitle>
                  {auditData.linked_records && Object.keys(auditData.linked_records).length > 0 && (
                    <div className="flex gap-2 mt-1">
                      {Object.entries(auditData.linked_records).map(([k, v]) => (
                        <span key={k} className="text-[10px] px-2 py-0.5 rounded bg-[#0d0d14] text-slate-500">{k}: {v}</span>
                      ))}
                    </div>
                  )}
                </CardHeader>
                <CardContent className="p-0">
                  <div className="divide-y divide-[#1e1e2e]">
                    {(auditData.entries || []).map((e, i) => (
                      <div key={i} className="flex items-center gap-3 px-4 py-2.5 text-xs" data-testid={`audit-entry-${i}`}>
                        <Clock className="h-3.5 w-3.5 text-slate-600 shrink-0" />
                        <span className="text-slate-400 w-32 shrink-0">{e.timestamp ? new Date(e.timestamp).toLocaleString() : '—'}</span>
                        <Badge variant="outline" className="text-[10px] text-slate-400 border-slate-700 shrink-0">{e.action}</Badge>
                        <span className="text-slate-500 truncate flex-1">by {e.user_id?.slice(0, 8)}...</span>
                        {e.meta && Object.keys(e.meta).length > 0 && (
                          <span className="text-[10px] text-slate-600 truncate max-w-[200px]">{JSON.stringify(e.meta).slice(0, 60)}</span>
                        )}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
